import * as React from 'react';

import { getInitialTypewriterState, getNextTypewriterStep, getTypewriterText } from 'shared/utils/typewriter';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Перебирает плейсхолдеры с эффектом печатной машинки. Вся логика фаз и таймингов живёт
 * в `shared/utils/typewriter`, здесь только таймеры и состояние компонента.
 *
 * Возвращает `null`, когда анимировать нечего — вызывающий код показывает статичный текст.
 * Флаг `disabled` останавливает перебор.
 *
 * `isForced` перебивает системную настройку «уменьшить движение»: она нужна, чтобы анимацию можно
 * было посмотреть на устройстве, где движение отключено на уровне ОС — например при демонстрации
 * или в автотестах, которые гоняются с reduce-motion.
 */
export function useTypewriterPlaceholder(placeholders: string[], disabled: boolean, isForced = false): string | null {
  const [text, setText] = React.useState(() => placeholders[0] ?? '');

  React.useEffect(() => {
    if (disabled || placeholders.length === 0 || (!isForced && prefersReducedMotion())) {
      return;
    }

    let timeoutId = 0;
    let isCancelled = false;
    let state = getInitialTypewriterState(placeholders);

    function tick(): void {
      const step = getNextTypewriterStep(placeholders, state);

      timeoutId = window.setTimeout(() => {
        if (isCancelled) {
          return;
        }

        state = step.state;
        setText(step.text);
        tick();
      }, step.delayMs);
    }

    setText(getTypewriterText(placeholders, state));
    tick();

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [placeholders, disabled, isForced]);

  if (disabled || placeholders.length === 0) {
    return null;
  }

  return text;
}
